import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Construction, ArrowLeft, Phone } from 'lucide-react';
import { ksbEquipmentData } from '../../data/ksb';
import './KSBSubpage.css';

const KSBEquipmentPage = () => {
  const { category, equipment } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=¡Hola!%20Quiero%20consultar%20sobre%20KSB`;

  // Build the key to lookup in ksbEquipmentData
  // Handle both /ksb/bombas/catalogo and /ksb/service patterns
  let dataKey;
  if (equipment) {
    dataKey = `${category}/${equipment}`;
  } else if (category) {
    dataKey = category;
  } else {
    // Extract from pathname for routes like /ksb/presurizacion
    const pathParts = location.pathname.split('/').filter(x => x);
    if (pathParts[0] === 'ksb' && pathParts[1]) {
      dataKey = pathParts[1];
    }
  }

  const data = ksbEquipmentData[dataKey];
  
  // Page titles for standalone pages
  const standaloneTitles = {
    'presurizacion': {
      title: 'Sistemas de presurización (Hyamat)',
      description: 'Equipos automáticos que mantienen presión constante en redes de agua con control inteligente y alta eficiencia.',
      categoryTitle: 'KSB'
    },
    'monitoreo': {
      title: 'Monitoreo (KSB Guard)',
      description: 'Sensores que monitorean bombas en tiempo real y alertan fallas para evitar paradas inesperadas.',
      categoryTitle: 'KSB'
    },
    'service': {
      title: 'Service, puesta en marcha y asesoramiento',
      description: 'Soporte técnico para instalación, arranque y mantenimiento de equipos industriales.',
      categoryTitle: 'KSB'
    }
  };

  const pageData = data || standaloneTitles[dataKey];
  
  if (!pageData) {
    return (
      <div className="ksb-construction-page" data-testid="ksb-not-found">
        <section className="ksb-construction-hero">
          <h1 className="ksb-construction-hero-title">Página no encontrada</h1>
          <p className="ksb-construction-hero-description">
            El contenido que busca no está disponible.
          </p>
        </section>
        <div className="ksb-construction-content">
          <div className="ksb-construction-box">
            <Button 
              onClick={() => navigate('/ksb')}
              className="ksb-construction-button"
            >
              <ArrowLeft className="mr-2" size={18} />
              Volver a KSB
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Determine back navigation
  const getBackPath = () => {
    if (pageData.category === 'bombas') return '/ksb/bombas';
    if (pageData.category === 'valvulas') return '/ksb/valvulas';
    return '/ksb';
  };

  const getBackLabel = () => {
    return pageData.categoryTitle || 'KSB';
  };

  return (
    <div className="ksb-construction-page" data-testid="ksb-equipment-page">
      {/* Hero Section */}
      <section className="ksb-construction-hero">
        <h1 className="ksb-construction-hero-title">{pageData.title}</h1>
        <p className="ksb-construction-hero-description">{pageData.description}</p>
      </section>

      {/* References heading */}
      {pageData.referencesHeading && (
        <div className="ksb-references-header">
          <span className="ksb-references-line" />
          <h2 className="ksb-references-title">{pageData.referencesHeading}</h2>
          <span className="ksb-references-line" />
        </div>
      )}

      {/* Under Construction Content */}
      <div className="ksb-construction-content">
        <div className="ksb-construction-box">
          <Construction size={64} className="ksb-construction-icon" />
          <h2 className="ksb-construction-title">Página en construcción</h2>
          <p className="ksb-construction-text">
            Estamos trabajando en el contenido de esta sección. 
            Mientras tanto, podés contactarnos para más información.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Button 
              onClick={() => window.open(whatsappLink, '_blank')}
              className="ksb-construction-button"
              data-testid="construction-contact-button"
            >
              <Phone className="mr-2" size={18} />
              Contactanos
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate(getBackPath())}
            >
              <ArrowLeft className="mr-2" size={18} />
              Volver a {getBackLabel()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KSBEquipmentPage;
